import { EventEmitter, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {NbDialogRef, NbDialogService} from '@nebular/theme';
import { Observable } from 'rxjs';
import { PermissionsApi } from '../../../../shared/components/permissions/api/permissions.api';
import { UpdateAccessOnAsset } from '../../../../shared/components/permissions/models/updateAccessOnAsset.model';
import { ToastrWrapperService } from '../../../../shared/services/toastr-wrapper.service';
import { ManagementApi } from '../api/management.api';
import { ChangePassword } from '../models/changePassword.model';
import { DeleteUser } from '../models/deleteUser.model';
import { GetUserInfo } from '../models/getUserInfo.model';
import { InviteUser } from '../models/inviteUser.model';
import { UpdateUserInfo } from '../models/updateUserInfo.model';
import { UpdateUserSetting } from '../models/updateUserSetting.model';
import { Membership } from '../models/membership.model';
import { UpdateMembership } from '../models/updateMembership.mode';
import { UserAccess } from '../models/user-access.model';
import { ManagementState } from '../state/management.state';
import { fromJS } from 'immutable';
import { Role } from '../models/role.model';
import { map } from 'rxjs/operators';
import { environment } from '../../../../../../environments/environment';
import { GeneralSetting } from '../models/general-setting.model';
import { LoginIds, RegisterRequirements, UsersSetting } from '../models/users-setting.model';
import { LogoService } from '../../../../shared/services/logo.service';
import { UserService } from '../../../../shared/services/user.service';
import {UserGroup} from '../models/user-group.model';
import {SubmitType} from '../../../../shared/models/submit.type';
import {Workspace} from '../../../../shared/models/work-space.model';
import {User} from '../models/user.model';
import {WorkSpaceService} from '../../../../shared/services/work-space.service';
import {UserModel} from '../../../../shared/models/user.model';
import {PasswordErrorType} from '../models/password-error.type';
import { UserTableState } from '../models/userTableState.model';
import {UserFilterService} from '../../../../shared/services/user-filter.service';
import {UserFilter} from '../../../../shared/models/user-filter.model';
import {UserFilterRecipients} from '../../../../shared/models/userInUserfilter.model';
import {VerifyTwoFactorDialogComponent}
    from '../components/user-management/dialogs/verify-two-factor/verify-two-factor-dialog.component';
import {MessageDialogComponent} from '../../../../shared/components/message-dialog/message-dialog.component';
import { UsersMetaData } from '../models/users-meta-data.model';
import {ConnectionForm} from '../../../../shared/models/connection-form.model';
import {ConnectionTestResponse} from '../models/connection-test-response.model';
import {Connection} from '../models/Connection.model';
import { AssetsMetaData } from '../../asset/models/assets-meta-data.model';
import {SortOrder} from '../../../../shared/models/sort.order';
import { UserDashboardModel } from '../models/userDashboardList.model';
import { UserSetting } from '../models/userSetting.model';
import {AuthState} from "../../../../auth/state/auth.state";

@Injectable()
export class ManagementFacade {
  constructor(
    private managementState: ManagementState,
    private managementApi: ManagementApi,
    private permissionApi: PermissionsApi,
    private router: Router,
    private toastr: ToastrWrapperService,
    private logoService: LogoService,
    private userService: UserService,
    private workspaceService: WorkSpaceService,
    private userFilterService: UserFilterService,
    private dialogService: NbDialogService,
    private authState: AuthState
  ) {}



  loadUserMinInfo(): void {
    this.userService.getUserMinState().subscribe(user => this.managementState.setUserMinInfo(user));
  }

  getUserMinInfo$(): Observable<UserModel> {
    return this.managementState.getUserMinInfo$();
  }

  loadSelectedWorkspace(workspaceId: string): void {
    this.workspaceService.updateSelectedWorkSpaceById(workspaceId);
    this.workspaceService.getSelectedWorkspace$().subscribe(workspace => this.managementState.setActiveWorkspace(workspace));
  }

  getActiveWorkspace$(): Observable<Partial<Workspace>> {
    return this.managementState.getActiveWorkspace$();
  }

  loadUsersMetaData(workspaceId: string, searchKey?: string): void {
    this.managementApi.getUsersMetadata(workspaceId, searchKey).subscribe(
      (res) => {
        this.managementState.setUsersMetaData(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadUserGroupMetaData(workspaceId: string, userGroupId: string): void {
    this.managementApi.getUserGroupMetadata(workspaceId, userGroupId).subscribe(
      (res) => {
        const metaData: UsersMetaData = {
          totalUsersCount: res.data.usersCount,
          deletedUsersCount:undefined,
          notApprovedUsersCount:undefined,
          userGroupsCount:undefined,
          withoutWorkspaceUsersCount:undefined,
        };
        this.managementState.setUsersMetaData(metaData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadUserAccessMetadata(workspaceId: string, userId: string, searchKey: string): void {
    this.managementApi.getUserAccessMetadata(workspaceId, userId, searchKey).subscribe(
      (res) => {
        this.managementState.setUserAssetsMetaData(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getUsersMetaData$(): Observable<UsersMetaData> {
    return this.managementState.getUsersMetaData$();
  }

  getUserAssetsMetaData$(): Observable<AssetsMetaData> {
    return this.managementState.getUserAssetsMetaData$();
  }

  // setting
  loadGeneralSetting() {
    this.managementApi.getGeneralSettings().pipe(map(res => res.data)).subscribe(
      (setting) => {
        this.managementState.updateGeneralSetting(setting);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateGeneralSetting(newSetting: GeneralSetting, alertSaveSettingDialog?: NbDialogRef<any>): void {
    this.managementApi.updateGeneralSetting(newSetting).subscribe(
      () => {
        this.managementState.updateGeneralSetting(newSetting);
        this.logoService.setLogo(newSetting.iconPath);
        this.toastr.showSuccess('تغییرات با موفقیت ذخیره شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        if(alertSaveSettingDialog){
          alertSaveSettingDialog.close();
        }
      },
    );
  }

  getGeneralSetting$(): Observable<GeneralSetting> {
    return this.managementState.getGeneralSetting$();
  }

  loadUsersSetting() {
    this.managementApi.getUsersSetting().pipe(map(res => res.data)).subscribe(
      (usersSetting: UsersSetting) => {
        this.managementState.setUsersSetting(usersSetting);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateUsersSetting(setting: UsersSetting, alertSaveSettingDialog?: NbDialogRef<any>): void {
    this.managementState.updateUsersSetting(setting);
    this.managementApi
      .updateUsersSetting(setting)
      .subscribe(
        () => {
          this.toastr.showSuccess('تغییرات با موفقیت ذخیره شد.');
        },
        (errorRes) => {
            if(errorRes.error.showPopUp) {
                this.dialogService.open(MessageDialogComponent, {
                    closeOnBackdropClick: false, context: {
                        message: errorRes.error.message,
                        title: 'خطا',
                        icon: 'error',
                    },
                }).onClose.subscribe(() => this.managementState.rollBackUsersSetting());
            } else {
                this.managementState.rollBackUsersSetting();
                this.toastr.showError(errorRes.error.message);
            }
        },
        () => {
          if(alertSaveSettingDialog){
            alertSaveSettingDialog.close();
          }
        },
      );
  }

  loadRegisterRequirements() {
    this.managementApi.getActiveIdentitySettings().pipe(map(res => res.data)).subscribe(
      (resData: any) => {
        this.managementState.setRegisterRequirements(resData.requirements);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getRegisterRequirements$(): Observable<RegisterRequirements[]> {
    return this.managementState.getRegisterRequirements$();
  }

  getUsersSetting$(): Observable<UsersSetting> {
    return this.managementState.getUsersSetting$();
  }

  loadDefaultRelationsSetting() {
    this.managementState.updateUsersSetting(null);
    this.managementApi.getDefaultRelationsSetting().subscribe(
      (data) => {
        this.managementState.updateDefaultRelationsSetting(data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadRelationsSetting() {
    this.loadDefaultRelationsSetting();
    this.managementApi.getRelationsSetting().subscribe(
      (data) => {
        this.managementState.updateRelationsSetting(data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateRelationsSetting(setting: any, workspaceid: any) {
    this.managementState.updateRelationsSetting(setting);
    this.managementApi.updateRelationsSetting(setting).subscribe(
      () => {
        this.router.navigate(['/home', workspaceid]);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackRelationsSetting();
      },
    );
  }

  getRelationsSetting$(): Observable<any> {
    return this.managementState.getRelationsSetting$();
  }

  loadRolesSetting() {
    this.managementApi.getRoles().subscribe(
      (res) => {
        this.managementState.updateRolesSetting(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getRolesSetting$(): Observable<Role[]> {
    return this.managementState.getRolesSetting$();
  }

  uploadLogo(file: File, newSetting: GeneralSetting, checkChanged: EventEmitter<any>) {
    this.managementApi
      .uploadFile('ApplicationFiles', environment.allWorkspaceId, file)
      .pipe(map(res => res.data))
      .subscribe(
        (res) => {
          newSetting.iconPath = res.path;
          checkChanged.emit(newSetting);
          this.managementState.updateGeneralSettingIconPath(newSetting);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
        () => {},
      );
  }

  removeLogo(setting: GeneralSetting, checkChanged: EventEmitter<any>) {
    this.managementApi.DeleteLogo()
    .subscribe(
      (res) => {
        setting.iconPath = null
        checkChanged.emit(setting);
        this.logoService.setLogo(setting.iconPath);
        this.managementState.updateGeneralSettingIconPath(setting);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      }
    )
  }

  uploadWorkspaceImage(file: File, newSetting: GeneralSetting, checkChanged: EventEmitter<any>) {
    this.managementApi
      .uploadFile('SelectWorkspaceImages', environment.allWorkspaceId, file)
      .pipe(map(res => res.data))
      .subscribe(
        (res) => {
          newSetting.selectWorkspaceImages = newSetting.selectWorkspaceImages.filter(img => img.isDefault).map(img => {
            img.selected = false;
            return img;
          });
          newSetting.selectWorkspaceImages.push({filePath: res.path,selected: true, isDefault: false});
          checkChanged.emit(newSetting);
          this.managementState.updateGeneralSettingIconPath(newSetting);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
        () => {},
      );
  }

  uploadBackgroundImage(file: File, newSetting: GeneralSetting, checkChanged: EventEmitter<any>) {
    this.managementApi
      .uploadFile('ApplicationFiles', environment.allWorkspaceId, file)
      .pipe(map(res => res.data))
      .subscribe(
        (res) => {
          newSetting.imageBackgroundPath = res.path;
          checkChanged.emit(newSetting);
          this.managementState.updateGeneralSettingIconPath(newSetting);
        },
        (err) => {
          this.toastr.showError(err.error.message);
        },
        () => {},
      );
  }

  loadRoles(workspaceId: string = environment.allWorkspaceId) {
    const allWorkspaceId: string = environment.allWorkspaceId;
    if(workspaceId === allWorkspaceId) {
      this.loadAllRoles();
    } else {
      this.loadWorkspaceRoles(workspaceId);
    }
  }

  loadAllRoles(): void {
    this.managementApi.getRoles().subscribe(
      (res) => {
        this.managementState.updateRoles(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadWorkspaceRoles(workspaceId: string): void {
    this.managementApi.getWorkspaceRoles(workspaceId).subscribe(
      (res) => {
        this.managementState.updateRoles(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );

  }

  loadWorkSpaces(roles) {
    this.managementApi.getWorkspaces().subscribe(
      (workspaces) => {
        this.managementState.updateWorkSpaces(
          this.managementState.addExtraInfoWorkSpaces(workspaces.data, roles),
        );
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadUserWorkSpaces() {
    this.managementApi.getUserWorkSpaces().subscribe(
      (workspaces) => {
        this.managementState.updateAllWorkSpaces(workspaces.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadWorkspacesWithCreateDashboardGroupPermission() {
    this.managementApi.getWorkspacesWithCreateDashboardGroupPermission().subscribe(
      (workspaces) => {
        this.managementState.updateWorkspacesWithCreateDashboardGroupPermission(workspaces.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }
  
  loadUsers(loadUsers: any): void {
    this.managementApi.getUsersWorkSpace(loadUsers).subscribe(
      (res) => {
        this.managementState.updateUserPageNumber(loadUsers.pageNumber);
        if(loadUsers.needCount) {
          this.managementState.updateUserTotalPages(res.data.totalPages);
        }
        this.managementState.updateUsersWorkSpace(res.data.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadNotApproveUsers(loadUsers: any): void {
    this.managementApi.getNotApproveUsers(loadUsers).subscribe(
      (res) => {
        this.managementState.updateUserPageNumber(loadUsers.pageNumber);
        if (loadUsers.needCount) {
          this.managementState.updateUserTotalPages(res.data.totalPages);
        }
        this.managementState.updateUsersWorkSpace(res.data.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadWithoutWorkspaceUsers(loadUsers: any): void {
    this.managementApi.getWithoutWorkspaceUsers(loadUsers).subscribe(
      (res) => {
        this.managementState.updateUserPageNumber(loadUsers.pageNumber);
        if (loadUsers.needCount) {
          this.managementState.updateUserTotalPages(res.data.totalPages);
        }
        this.managementState.updateUsersWorkSpace(res.data.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadDeletedUsers(loadUsers: any): void {
    this.managementApi.getAllDeletedUsers(loadUsers).subscribe(
      (res) => {
        this.managementState.updateUserPageNumber(loadUsers.pageNumber);
        if (loadUsers.needCount) {
          this.managementState.updateUserTotalPages(res.data.totalPages);
        }
        this.managementState.updateUsersWorkSpace(res.data.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getUsersWorkSpace$(): Observable<Partial<User>[]> {
    return this.managementState.getUsersWorkSpace$();
  }

  loadUserUserFilters(workspaceId: string, userId: string, searchKey: string, pageNumber: number,
                      pageSize: number, needCount: boolean, isLoading: boolean = false): void {
      this.userFilterService.getUsersUserFilters(workspaceId, userId, searchKey, pageNumber, pageSize, needCount)
          .subscribe(
              (response) => {
                  this.managementState.updateUserFilters(response.data.pageData);
                  this.managementState.updateUserFilterPageNumber(pageNumber);
                  if(needCount) {
                      this.managementState.updateUserFilterTotalPages(response.data.totalPages);
                  }
                  if(isLoading) {
                      this.setLoadingData(false);
                  }
              },
              (errorRes) => {
                  this.toastr.showError(errorRes.error.message);
              },
          );
  }

  loadUserGroupUserFilters(workspaceId: string, userGroupId: string, searchKey: string, pageNumber: number,
                           pageSize: number, needCount: boolean): void {
      this.userFilterService.getUserGroupsUserFilters(workspaceId, userGroupId, searchKey, pageNumber, pageSize, needCount)
          .subscribe(
              (response) => {
                  this.managementState.updateUserFilters(response.data.pageData);
                  this.managementState.updateUserFilterPageNumber(pageNumber);
                  if(needCount) {
                      this.managementState.updateUserFilterTotalPages(response.data.totalPages);
                  }
              },
              (errorRes) => {
                  this.toastr.showError(errorRes.error.message);
              },
          );
  }

  getUserFilters$(): Observable<Partial<UserFilter>[]> {
      return this.managementState.getUserFilters$();
  }

   removeUseFilter(userFilter: Partial<UserFilter>): void {
      this.setSubmitState('submitting');
      const userFilterRecipients: Partial<UserFilterRecipients>[] = [
          {
              userFilterRecipientStatus: 'Removed',
              userFilterRecipientId: this.managementState.userOrGroupId,
              userFilterRecipientType: this.managementState.userFilterRecipientType,
          },
      ];
      this.userFilterService.updateUseFilter(userFilter.userFilterId,userFilter.columnId,userFilter.name,
          userFilterRecipients, this.managementState.workspaceId, userFilter.dataSourceId).subscribe(
          () => {
              this.setSubmitState('submitted');
              this.toastr.showSuccess('تغییرات با موفقیت ذخیره شد.');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  loadUserGroupsWithUserStatus(bodyRequest: any): void {
    this.managementApi.getUserGroupsWithUserStatus(bodyRequest).pipe(map(res => res.data)).subscribe(
      (res) => {
        if(bodyRequest.needCount) {
          this.managementState.updateUserGroupsTotalPages(res.totalPages);
        }
        this.managementState.updateUserGroupsPageNumber(bodyRequest.pageNumber);
        this.managementState.updateUserGroupsWithUserStatus(res.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getUserGroupsWithUserStatus$(): Observable<Partial<UserGroup>[]> {
    return this.managementState.getUserGroupsWithUserStatus$();
  }

  loadUserGroups(bodyRequest: any) {
    this.managementApi.getAllUserGroups(bodyRequest).pipe(map((res: any) => res.data)).subscribe(
      (data) => {
        if(bodyRequest.needCount) {
          this.managementState.updateUserGroupsTotalPages(data.totalPages);
        }
        this.managementState.updateUserGroupsPageNumber(bodyRequest.pageNumber);
        this.managementState.updateUserGroups(data.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getUserGroups$(): Observable<UserGroup[]> {
    return this.managementState.getUserGroups$();
  }

  getUserGroupsTotalPages$(): Observable<number> {
    return this.managementState.getUserGroupsTotalPages$();
  }

  getUserGroupsPageNumber$(): Observable<number> {
    return this.managementState.getUserGroupsPageNumber$();
  }

  loadUserSelect(body: GetUserInfo) {
    this.managementState.updateUserSelect(null);
    this.managementApi.getUserInfo(body).subscribe(
      (res) => {
        this.managementState.updateUserSelect(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  uploadUserAvatar(workspaceId: string, file, body: UpdateUserInfo) {
    this.managementApi
      .uploadFile('UserAvatars', workspaceId, file)
      .subscribe(
        (res) => {
          if (res.isSuccess) {
            body.avatarPath = res.data.path;
            this.updateUserInfo(body);
          }
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
        () => {},
      );
  }

  changePassword(body: ChangePassword) {
    this.managementApi.changePassword(body).subscribe(
      (res) => {
        if (res.isSuccess) {
          this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
          this.managementState.setPasswordErrorState('NoError');
        }
      },
      (res) => {
        this.managementState.setPasswordErrorState(res.error.message);
        this.toastr.showError(res.error.message);
      },
      () => {},
    );
  }

  loadUserAccess(loadAccess: any, loading: boolean = false): void {
    this.managementState.updatePageNumber(loadAccess.pageNumber);
    this.managementState.updateUserAllAccess(null);
    this.managementApi.getUserAllAccess(loadAccess).subscribe(
      (res) => {
        if(loadAccess.needCount) {
          this.managementState.updateTotalPages(res.data.totalPages);
        }
        this.managementState.updateUserAllAccess(this.managementState.addExtraInfoAccess(res.data.pageData));
        if(loading) {
            this.setLoadingData(false);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadUsersInUserGroupSelected(loadUsers: any): void {
    this.managementApi.getUsersInUserGroup(loadUsers).pipe(map(res => res.data)).subscribe(
      (res) => {
        if(loadUsers.needCount) {
          this.managementState.updateTotalPages(res.totalPages);
        }
        this.managementState.updatePageNumber(loadUsers.pageNumber);
        this.managementState.updateUsersOfUserGroupSelected(res.pageData);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadSelectedUserGroup(workspaceId: string, userGroupId: string): void {
    this.managementState.userOrGroupId = userGroupId;
    this.managementState.userFilterRecipientType = 'Group';
    this.managementApi.getUserGroup(workspaceId, userGroupId).pipe(map(res => res.data)).subscribe(
      (userGroup) => {
        this.managementState.updateUserGroupSelected(userGroup);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadUserGroupAllAccess(bodyRequest: any): void {
    this.managementApi.getUserGroupAllAccess(bodyRequest).pipe(map(res => res.data)).subscribe(
      (res) => {
        if(bodyRequest.needCount) {
          this.managementState.updateAccessTotalPages(res.totalPages);
        }
        this.managementState.updateAccessPageNumber(bodyRequest.pageNumber);
        this.managementState.updateUserGroupAllAccess(this.managementState.addExtraInfoAccess(res.pageData));
      },
      () => {},
    );
  }

  loadUsersNotInUserGroup(loadUsers: any): void {
      this.managementApi.getUsersNotInUserGroup(loadUsers).pipe(map(res => res.data)).subscribe(
        (res) => {
          if(loadUsers.needCount) {
            this.managementState.updateAddUsersTotalPages(res.totalPages);
          }
          this.managementState.updateAddUsersPageNumber(loadUsers.pageNumber);
          this.managementState.updateUsersNotInUserGroup(res.pageData);
        },
        (errorRes) => {
          this.toastr.showError(errorRes.error.message);
        },
      );
  }

  getUsersNotInUserGroup$(): Observable<Partial<User>[]> {
    return this.managementState.getUsersNotInUserGroup$();
  }

  getAddUsersPageNumber$(): Observable<number> {
    return this.managementState.getAddUsersPageNumber$();
  }

  getAddUsersTotalPages$(): Observable<number> {
    return this.managementState.getAddUsersTotalPages$();
  }

  loadWorkSpaceRoles(workspaceId: string,type: string): void {
    this.permissionApi.getWorkspaceRoles(workspaceId).subscribe(
      (res) => {
        this.managementState.updateWorkspaceRoles(res.data,type);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadUserWorkSpaceMemberships(body: any): void {
    this.managementState.updateUserWorkSpaceMemberships(null);
    this.managementApi.getUserWorkSpaceMemberships(body).subscribe(memberships => {
      this.managementState.
      updateUserWorkSpaceMemberships(this.managementState.addExtraInfoMemberships(memberships.data.pageData));
      if(body.pageNumber === 1) {
        this.managementState.updateTotalPages(memberships.data.totalPages);
      }
    },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
    });
  }

  updateUserInfo(body: UpdateUserInfo) {
    this.managementApi.updateUserInfo(body).subscribe(
      (res) => {
        if (res.isSuccess) {
          this.toastr.showSuccess();
          this.managementState.updateUserSelect(res.data);
          this.userService.updateUserMinState(res.data);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  loadUserSetting(workspaceId: string, userId: string) {
    this.managementState.userOrGroupId = userId;
    this.managementState.userFilterRecipientType = 'Solo';
    this.managementApi.getUserSetting(workspaceId, userId).subscribe(
      (res) => {
        if (res.isSuccess) {
          this.managementState.updateUserSetting(res.data);
        }
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  updateUserSetting(body: UpdateUserSetting) {
    this.managementApi.updateUserSetting(body).subscribe(
      (res) => {
        const userSetting: UserSetting = {
          id: body.userId,
          customLoginPage: body.customLoginPage,
          description: body.description,
          expirationDate: body.expirationDate,
          inActive: body.inActive,
          hasRefreshTokenDefault: body.hasRefreshTokenDefault,
          refreshTokenDuration: body.refreshTokenDuration,
          hasAccessIp: body.hasAccessIp,
          accessIps: body.accessIps,
        };
        this.managementState.updateUserSetting(userSetting);
        this.toastr.showSuccess('تغییرات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  addIPAccess(userId: string, ipAddress: string) {
    this.managementApi.addIPAccess(userId, ipAddress).subscribe(
      (res) => {
        this.loadIpAccessList(userId);
        this.toastr.showSuccess('تغییرات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  deleteIPAccess(userId: string, ipAddress: string) {
    this.managementApi.deleteIPAccess(userId, ipAddress).subscribe(
      (res) => {
        this.loadIpAccessList(userId);
        this.toastr.showSuccess('تغییرات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  updateHasIpAccess(userId: string, hasIPAccess: boolean) {
    this.managementApi.updateHasIpAccess(userId, hasIPAccess).subscribe(
      (res) => {
        this.toastr.showSuccess('تغییرات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  getUserSetting$(): Observable<any> {
    return this.managementState.getUserSetting$();
  }

  loadUserDashboardList(userId: string): void {
    this.managementApi.getUserDashboards(userId).subscribe(
      (res) => {
        this.managementState.updateUserDashboardList(res.data);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  loadIpAccessList(userId: string): void {
    this.managementApi.getIPAccessList(userId).subscribe(
      (res) => {
        this.managementState.updateIPAccessList(res.data?.ipsAccess);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  getIPAccessList$(): Observable<string[]> {
    return this.managementState.getIPAccessListList$();
  }

  getUserDashboardList$(): Observable<UserDashboardModel[]> {
    return this.managementState.getUserDashboardList$();
  }

  updateUserGroupSelected(userGroup: UserGroup): void {
    this.managementApi.updateUserGroupName(userGroup.userGroupId,userGroup.userGroupName,userGroup.workspaceId).
    subscribe(
      () => {
        userGroup.lastModifiedTickTime = new Date().getTime()* 10000 + 621355968000000000;
        this.managementState.updateUserGroupSelected(userGroup);
        this.toastr.showSuccess();
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateRole(role: Role, roleActionDialog: NbDialogRef<any>) {
    const tempRole = fromJS(role).toJS() as unknown as Role;
    tempRole.permissions = this.mapPermissions(role.permissions);
    this.managementApi.updateRole(tempRole).subscribe(
      () => {
        this.managementState.updateRoleSetting(role);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        roleActionDialog.close(true);
      },
      () => {
        roleActionDialog.close(true);
      },
    );
  }

  setTokenUserDuration(payload: {usersId: string[];tokenDuration: number;allUsers: boolean;allGroupUsers?: boolean}) {
    this.managementApi.setTokenUserDuration(payload).subscribe(
      () => {
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateTokenDuration(payload: {workspaceId: string;userGroupId: string;refreshTokenDuration: number;
    allGroupUsers?: boolean;}) {
    this.managementApi.updateTokenDuration(payload).subscribe(
      () => {
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  updateWorkSpaceName(workspace: Partial<Workspace>, editWorkspaceNameDialog: NbDialogRef<any>,
    selectWorkspacePageMode = false, sortOrder: SortOrder = 'None') {
    this.managementApi.updateWorkspaceName(workspace).subscribe(
      () => {
        if (!selectWorkspacePageMode){
          this.managementState.updateWorkSpace(workspace);
          this.loadUserWorkSpaces();
          this.workspaceService.updateUserWorkspace(workspace);
        } else {
          this.workspaceService.loadUserWorkSpaces(sortOrder);
        }
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        editWorkspaceNameDialog.close();
      },
    );
  }

  updateWorkspaceRoles(changeWorkSpace: any, rolesWorkspaceDialog: NbDialogRef<any>) {
    this.managementApi.updateWorkspaceRoles(changeWorkSpace).subscribe(
      () => {
        this.managementState.updateWorkSpace(changeWorkSpace);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        rolesWorkspaceDialog.close();
      },
      );
  }

  activateWorkspace(workspaceUpdate: any) {
    this.managementApi.activateWorkspace(workspaceUpdate).subscribe(
      () => {
        this.managementState.updateWorkSpace(workspaceUpdate);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.workspaceService.loadUserWorkSpaces();
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  deactivateWorkspace(workspaceUpdate: any) {
    this.managementApi.deactivateWorkspace(workspaceUpdate).subscribe(
      () => {
        this.managementState.updateWorkSpace(workspaceUpdate);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.workspaceService.loadUserWorkSpaces();
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  updateUserGroupName(userGroupId: string, name: string, workspaceId: string): void {
    this.setSubmitState('submitting');
    this.managementApi.updateUserGroupName(userGroupId, name, workspaceId).subscribe(
      () => {
        this.managementState.updateUserGroupName(userGroupId, name);
        this.toastr.showSuccess();
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  addUsersToUserGroup(workspaceId: string, userGroupId: string, users: Partial<User>[]): void {
    this.setSubmitState('submitting');
    this.managementApi.addUsersToUserGroup(workspaceId, userGroupId, users).subscribe(
      () => {
        this.managementState.addUsersToSelectedUserGroup(users);
        this.managementState.updateUserGroupSelectedDetails(users.length,true);
        this.toastr.showSuccess();
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  addUserToUserGroup(workspaceId: string, userGroupId: string, user: Partial<User>): void {
    this.managementState.addStatusUserToUserGroup(userGroupId);
    this.managementApi.addUsersToUserGroup(workspaceId, userGroupId, [user]).subscribe(
      () => {},
      (errorRes) => {
        this.managementState.rollBackUserGroups();
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  removeUserFromUserGroup(workspaceId: string, userGroupId: string, user: Partial<User>): void {
    this.managementState.removeStatusUserFromUserGroup(userGroupId);
    this.managementApi.deleteUsersOfUserGroup(workspaceId, userGroupId, [user.userId]).subscribe(
      () => {},
      (errorRes) => {
        this.managementState.rollBackUserGroups();
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  updateUserWorkspaceMembershipsState(memberships: Membership[]): void {
    this.managementState.updateUserWorkSpaceMemberships(memberships);
  }

  updateUserWorkSpaceMemberships(membership: UpdateMembership, dialogRef: NbDialogRef<any>, user?: any) {
    this.managementApi.updateUserWorkSpaceMemberships(membership).subscribe(
      () => {
        dialogRef.close();
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.managementState.updateUserWorkSpace(user);
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        dialogRef.close();
        this.toastr.showError(errorRes.error.message);
      },
      () => {},
    );
  }

  updateUserDashboardAccess(body: UpdateAccessOnAsset, access: Partial<UserAccess>) {
    this.managementState.updateSelectedAccess(access);
    this.managementApi.updateUserDashboardAccess(body).subscribe(
      () => {
        this.managementState.updateUserAccess(access);
        },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackAccess();
      },
      () => {},
    );
  }


  updateUserGroupDashboardAccess(body: UpdateAccessOnAsset, access: Partial<UserAccess>): void {
    this.managementState.updateSelectedAccess(access);
    this.managementApi.updateUserDashboardAccess(body).subscribe(
      () => {
        this.managementState.updateUserGroupAccess(access);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackAccess();
      },
      () => {},
    );
  }

  updateUserDataSourceAccess(body: UpdateAccessOnAsset, access: Partial<UserAccess>): void {
    this.managementState.updateSelectedAccess(access);
    this.managementApi.updateUserDataSourceAccess(body).subscribe(
      () => {
        this.managementState.updateUserAccess(access);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackAccess();
      },
      () => {},
    );
  }

  updateUserGroupDataSourceAccess(body: UpdateAccessOnAsset, access: Partial<UserAccess>): void {
    this.managementState.updateSelectedAccess(access);
    this.managementApi.updateUserDataSourceAccess(body).subscribe(
      () => {
        this.managementState.updateUserGroupAccess(access);
        },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackAccess();
      },
      () => {},
    );
  }

  updateUserDashboardGroupAccess(body: UpdateAccessOnAsset, access: Partial<UserAccess>): void {
    this.managementState.updateSelectedAccess(access);
    this.managementApi.updateUserDashboardGroupAccess(body).subscribe(
      () => {
        this.managementState.updateUserAccess(access);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackAccess();
      },
      () => {},
    );
  }

  updateUserGroupDashboardGroupAccess(body: UpdateAccessOnAsset, access: Partial<UserAccess>): void {
    this.managementState.updateSelectedAccess(access);
    this.managementApi.updateUserDashboardGroupAccess(body).subscribe(
      () => {
        this.managementState.updateUserGroupAccess(access);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackAccess();
      },
      () => {},
    );
  }

  updateSelectedAccess(access: Partial<UserAccess>) {
    this.managementState.updateSelectedAccess(access);
  }

  setLoadingData(loading: boolean): void {
      this.managementState.setLoadingData(loading);
  }

  getLoadingData$(): Observable<boolean> {
      return this.managementState.getLoadingData$();
  }

  updatePageNumber(pageNumber: number): void {
    this.managementState.updatePageNumber(pageNumber);
  }

  getRoles$(): Observable<any> {
    return this.managementState.getRoles$();
  }

  getWorkSpaces$(): Observable<Partial<Workspace>[]> {
    return this.managementState.getWorkSpaces$();
  }

  getAllWorkSpaces$(): Observable<Partial<Workspace>[]> {
    return this.managementState.getAllWorkSpaces$();
  }

  getWorkspacesWithCreateDashboardGroupPermission$(): Observable<Partial<Workspace>[]> {
    return this.managementState.getWorkspacesWithCreateDashboardGroupPermission$();
  }

  getUserSelect$(): Observable<any> {
    return this.managementState.getUserSelect$();
  }

  getUserGroupSelected$(): Observable<UserGroup> {
    return this.managementState.getUserGroupSelected$();
  }

  getUserAllAccess$(): Observable<Partial<UserAccess>[]> {
    return this.managementState.getUserAllAccess$();
  }

  getUsersInUserGroupSelected$(): Observable<Partial<User>[]> {
    return this.managementState.getUsersInUserGroupSelected$();
  }

  getUSerGroupAllAccess$(): Observable<Partial<UserAccess>[]> {
    return this.managementState.getUserGroupAllAccess$();
  }

  getDefaultRoles$(): Observable<any> {
    return this.managementState.getDefaultRoles$();
  }

  getUserWorkSpaceMamberships$(): Observable<any> {
    return this.managementState.getUserWorkSpaceMamberships$();
  }

  getSelectedAccess$(): Observable<Partial<UserAccess>> {
    return this.managementState.getSelectedAccess$();
  }

  getTotalPages$(): Observable<number> {
    return this.managementState.getTotalPages$();
  }

  getPageNumber$(): Observable<number> {
    return this.managementState.getPageNumber$();
  }

  getUserTotalPages$(): Observable<number>  {
    return this.managementState.getUserTotalPages$();
  }

  getUserPageNumber$(): Observable<number>  {
    return this.managementState.getUserPageNumber$();
  }

  getAccessTotalPages$(): Observable<number> {
    return this.managementState.getAccessTotalPages$();
  }

  getAccessPageNumber$(): Observable<number> {
    return this.managementState.getAccessPageNumber$();
  }

  getUserFilterPageNumber$(): Observable<number> {
      return this.managementState.getUserFilterPageNumber$();
  }

  getUserFilterTotalPages$(): Observable<number> {
      return this.managementState.getUserFilterTotalPages$();
  }

  deleteRoles(roles: any[], deleteRoleDialog: NbDialogRef<any>) {
    this.managementApi.deleteRole(roles).subscribe(
      () => {
        this.managementState.updateDeletedRoles(roles);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        deleteRoleDialog.close(true);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        deleteRoleDialog.close(false);
      },
    );
  }

  deleteWorkSpace(workspaceId: any, deleteWorkspaceDialog: NbDialogRef<any>) {
    this.managementApi.deleteWorkSpace(workspaceId).subscribe(
      () => {
        this.managementState.filterWorkSpaces(workspaceId);
        this.workspaceService.filterWorkspaces(workspaceId);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        deleteWorkspaceDialog.close(true);
      },
      () => {
        this.loadUserWorkSpaces();
        deleteWorkspaceDialog.close(true);
      },
    );
  }

  deleteUser(body: DeleteUser, state: UserTableState = 'withWorkspaceUsers'): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteUser(body).subscribe(
      () => {
        const loadUsers = {
          workspaceId: body.workspaceId,
          searchKey: '',
          pageNumber: 1,
          pageSize: 10,
          needCount: true,
        };
        if (state === 'withWorkspaceUsers'){
          this.loadUsers(loadUsers);
          const workSpace = this.workspaceService.getSelectedWorkspaceValue();
          this.toastr.showSuccess(
            'کاربر از ' + workSpace.name + ' حذف شد.',
          );
        } else {
          this.loadWithoutWorkspaceUsers(loadUsers);
          this.toastr.showSuccess(
            'کاربر حذف شد.',
          );
        }
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  deleteNotApproveduser(body): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteNotApprovedUser(body).subscribe(
      () => {
        const loadUsers = {
          searchKey: '',
          pageNumber: 1,
          pageSize: 10,
          needCount: true,
        };
        this.loadNotApproveUsers(loadUsers);
        this.toastr.showSuccess(
          'کاربر حذف شد.',
        );
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => { },
    );
  }
  deleteProfilePhoto(userId: string): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteProfilePhoto(userId).subscribe(
      (res) => {
        // if (res.isSuccess) {
        //   this.toastr.showSuccess(res.message ||'عکس کاربری با موفقیت حذف شد.');
        // } else {
        //   this.toastr.showError(res.message || 'خطا در حذف عکس کاربری.');
        // }
        // this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error?.message || 'خطا در حذف عکس کاربری.');
        this.setSubmitState('submitted');
      }
    );
  }
  deleteSelectedUser(body: DeleteUser): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteUser(body).subscribe(
      () => {
        this.managementState.updateUserSelect(null);
        this.toastr.showSuccess('کاربر حذف شد.');
        this.setSubmitState('submitted');
        },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  deleteUsersOfUserGroup(workspaceId: string, userGroupId: string, userIds: string[]): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteUsersOfUserGroup(workspaceId, userGroupId, userIds).subscribe(
      () => {
        const loadUsers = {
          workspaceId,
          userGroupId,
          searchKey: '',
          pageNumber: 1,
          pageSize: 9,
          needCount: true,
        };
        this.loadUsersInUserGroupSelected(loadUsers);
        this.managementState.updateUserGroupSelectedDetails(userIds.length,false);
        this.toastr.showSuccess();
        this.setSubmitState('submitted');
        },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
        },
      () => {},
    );
  }

  physicalDeleteUsers(userIds: string[]): void {
    this.setSubmitState('submitting');
    this.managementApi.removeUsers(userIds).subscribe(
      () => {
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
    );
  }

  deleteUserGroup(userGroupId: string, workspaceId: string ): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteUserGroup(userGroupId, workspaceId).subscribe(
      () => {
        this.managementState.filterUserGroupsWorkSpace(userGroupId);
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.setSubmitState('submitted');

      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');

      },
      () => {},
    );
  }

  deleteSelectedUserGroup(userGroupId: string, workspaceId: string ): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteUserGroup(userGroupId, workspaceId).subscribe(
      () => {
        this.managementState.updateUserGroupSelected(null);
        this.router.navigate([`./home/user-managment/${workspaceId}`],{queryParams: {selectedTab: 'userGroups'}});
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.setSubmitState('submitted');

      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');

      },
      () => {},
    );
  }

  deleteUserAccess(access: Partial<UserAccess>, userId: string, type: string, loadAllAccess: any): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteAccess(access, userId, type).subscribe(
      () => {
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.setSubmitState('submitted');
        loadAllAccess.userId = userId;
        loadAllAccess.pageNumber = 1;
        loadAllAccess.needCount = true;
        this.loadUserAccess(loadAllAccess);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
    );
  }

  deleteUserGroupAccess(access: Partial<UserAccess>, userGroupId: string, type: string, loadAllAccess: any): void {
    this.setSubmitState('submitting');
    this.managementApi.deleteAccess(access, userGroupId, type).subscribe(
      () => {
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        loadAllAccess.userGroupId = userGroupId;
        loadAllAccess.pageNumber = 1;
        loadAllAccess.needCount = true;
        this.loadUserGroupAllAccess(loadAllAccess);
        this.setSubmitState('submitted');
        },
      () => {
        this.toastr.showError('خطا در حذف مجوز دسترسی');
        this.managementState.rollBackGroupAssets();
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  restoreUser(userId: string): void {
    this.setSubmitState('submitting');
    this.managementApi.restoreUser(userId).subscribe(
      () => {
        this.toastr.showSuccess('کاربر بازگردانده شد.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => { },
    );
  }

  createRole(role: any) {
    role.permissions = this.mapPermissions(role.permissions);
    this.managementApi.createRole(role).pipe(map(res => res.data)).subscribe(
      (roleTemp) => {
        this.managementState.addRole(roleTemp);
        this.toastr.showSuccess('نقش با موفقیت ایجاد شد.');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
    );
  }

  createWorkSpace(workspace: any, dialogRef: NbDialogRef<any>) {
    this.managementApi.createWorkSpace(workspace).pipe(map(res => res.data)).subscribe(
      (res) => {
        workspace['workspaceId'] = res.workspaceId;
        workspace['imagePath'] = null;
        workspace['inActive'] = false;
        this.workspaceService.addWorkSpace(workspace);
        this.addWorkspace(workspace);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        dialogRef.close();
      },
    );
  }

  addWorkspace(workspace): void {
    this.managementState.addWorkSpace(workspace);
  }

  createUser(inviteUser, dialogRef: NbDialogRef<any>, workspaceId: any) {
    this.setSubmitState('submitting');
    this.managementApi.inviteUser(inviteUser).subscribe(
      () => {
        const loadUsers = {
          workspaceId,
          searchKey: '',
          pageNumber: 1,
          pageSize: 10,
          needCount: true,
        };
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        this.loadUsers(loadUsers);
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  approveUser(data: any): void {
    this.setSubmitState('submitting');
    this.managementApi.approveUser(data).subscribe(
      () => {
        this.toastr.showSuccess(
          'ثبت‌نام کاربر تایید شد.درصورت عضو نبودن در هیچ فضای کاری، به لیست کاربران بدون فضای کاری اضافه می‌شود.',
        );
        const loadUsers = {
          searchKey: '',
          pageNumber: 1,
          pageSize: 10,
          needCount: true,
        };
        this.loadNotApproveUsers(loadUsers);
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => { },
    );
  }

  setWorkspaceForNewUser(user: UpdateMembership) {
    this.managementApi.updateUserWorkSpaceMemberships(user).subscribe(
      () => {
        this.toastr.showSuccess('عملیات با موفقیت انجام شد.');
        const loadUsers = {
          searchKey: '',
          pageNumber: 1,
          pageSize: 10,
          needCount: true,
        };
        this.loadWithoutWorkspaceUsers(loadUsers);
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => { },
    );
  }

  createUserGroup(name: string, selectedWorkspace: Partial<Workspace>): void {
    this.setSubmitState('submitting');
    this.managementApi.createUserGroup(selectedWorkspace.workspaceId, name).pipe(map(res => res.data)).subscribe(
      (userGroupId) => {
        const userGroup: UserGroup = {
          userGroupId,
          workspaceId: selectedWorkspace.workspaceId,
          userGroupName: name,
          workspaceName: selectedWorkspace.name,
          userCount: 0,
          lastModifiedTickTime: Math.log(0),
          creationTickTime: Math.log(0),
          link: `/home/user-managment/group/${selectedWorkspace.workspaceId}/${userGroupId}`,
        };
        this.managementState.addGroup(userGroup);
        this.toastr.showSuccess('گروه کاربری ساخته شد.');
        this.setSubmitState('submitted');
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.setSubmitState('submitted');
      },
      () => {},
    );
  }

  restartGeneralSetting(restartSettingDialog?: NbDialogRef<any>): void {
    this.managementApi.setDefaultGeneralSetting().pipe(map(res => res.data)).subscribe(
      (defaultSetting) => {
        this.managementState.updateGeneralSetting(defaultSetting);
        this.logoService.setDefaultLogo();
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        restartSettingDialog.close(true);
      },
    );
  }

  restartUsersSetting(restartSettingDialog?: NbDialogRef<any>): void {
    this.managementApi.setDefaultUsersSetting().pipe(map(res => res.data)).subscribe(
      (usersSetting) => {
        this.managementState.setUsersSetting(usersSetting);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      },
      () => {
        restartSettingDialog.close(true);
      },
    );
  }

  restartRelationsSetting() {
    const defaultSetting = this.managementState.getDefaultRelationsSetting();
    this.managementState.updateRelationsSetting(defaultSetting);
    this.managementApi.updateRelationsSetting(defaultSetting).subscribe(
      () => {},
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
        this.managementState.rollBackRelationsSetting();
      },
    );
  }

  setDefaultUserAccess(): void {
    this.managementState.updatePageNumber(1);
    this.managementState.updateTotalPages(1);
    this.managementState.updateUserAllAccess(null);
    this.loadUserMinInfo();
  }

  setDefaultManagement() {
    this.managementState.updateUsersWorkSpace(null);
    this.managementState.updateUserGroups(null);
  }

  mapPermissions(permissions: any): any {
    const result =[];
    permissions.forEach(permission => {
      if(permission.isChecked) {
        result.push(permission.id);
      }
    });
    return result;
  }

  rollBackUsersSetting() {
    this.managementState.rollBackUsersSetting();
  }

  cancelUpdateUsersSetting(): void {
      this.managementState.cancelUpdateUsersSetting();
  }
  rollBackGeneralSetting() {
    this.managementState.rollBackGeneralSetting();
  }
  rollBackUserSetting() {
    this.managementState.rollBackUserSetting();
  }

  setSubmitState(submitState: SubmitType): void {
    this.managementState.setSubmitState(submitState);
  }

  getSubmitState$(): Observable<SubmitType> {
    return this.managementState.getSubmitState$();
  }

  setUserGroupManageDefaults() {
    this.managementState.updateUsersOfUserGroupSelected(null);
    this.managementState.updateUserGroupAllAccess(null);
    this.loadUserMinInfo();
  }

  loadSelectWorkspaceImage() {
    this.managementApi.getPublicGeneralSettings().subscribe(
      (res) => {
        this.authState.setGeneralSettings(res);
        this.managementState.setSelectWorkspaceImage(res.data.selectWorkspaceImagePath);
      },
      (errorRes) => {
        this.toastr.showError(errorRes.error.message);
      });
  }

  getSelectWorkspaceImage$() {
    return this.managementState.getSelectWorkspaceImage$();
  }

  getPasswordErrorState$(): Observable<PasswordErrorType> {
    return this.managementState.getPasswordErrorState$();
  }

  updatePhoneNumber(phoneNumber: string, workspaceId: string, userId: string): void {
      this.managementApi.updatePhoneNumber(phoneNumber, workspaceId, userId).subscribe(
          (response) => {
              if(response.data.shouldVerifyPhoneNumber) {
                  this.managementState.updateTemporaryPhoneNumber(phoneNumber, response.data.phoneNumber,
                      response.data.referenceId);
                  this.dialogService.open(VerifyTwoFactorDialogComponent,
                      {closeOnBackdropClick: false,context: {workspaceId}}).onClose.subscribe(() =>
                      this.managementState.updateConfirmationOTP(null));
              }
          },
      );
  }

  getTemporaryPhoneNumber$(): Observable<{ value: string; showValue: string; referenceId: string}> {
      return this.managementState.getTemporaryPhoneNumber$();
  }

  verifyPhoneNumber(otp: string, workspaceId: string,
                    temporaryPhoneNumber: { value: string; showValue: string; referenceId: string}): void {
      this.managementApi.verifyPhoneNumber(otp, workspaceId, temporaryPhoneNumber.referenceId,
          temporaryPhoneNumber.value).subscribe(
          () => {
            this.toastr.showSuccess();
            this.managementState.updateConfirmationOTP(true);
            this.managementState.updateConfirmationOTP(null);
            this.managementState.updateTemporaryPhoneNumber(undefined, undefined, undefined);
          },
          () => {
            this.managementState.updateConfirmationOTP(false);
          },
      );
  }

  getConfirmationOTP$(): Observable<boolean> {
    return this.managementState.getConfirmationOTP$();
  }

  resendOTP(referenceId: string, phoneNumber: string): void {
      this.managementApi.resendOTP(referenceId).subscribe(
          (response) => {
              this.managementState.updateTemporaryPhoneNumber(phoneNumber, response.phoneNumber,
                  response.referenceId);
              this.managementState.updateConfirmationOTP(null);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  resetPasswordErrorState() {
    this.managementState.setPasswordErrorState();
  }

  resetUserFilterState(): void {
      this.managementState.updateUserFilters([]);
  }

  loadDataBaseConnections(): void {
      this.managementApi.getDataBaseConnections().subscribe(
          (res) => {
              this.managementState.updateConnections(res.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getConnections$(): Observable<Partial<Connection>[]> {
      return this.managementState.getConnections$();
  }

  testConnection(connectionForm: Partial<ConnectionForm>): void {
      this.managementApi.testDBConnection(connectionForm).subscribe(
          (res) => {
              this.managementState.setConnectionTestResponse(res.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  createConnection(connectionForm: Partial<ConnectionForm>): void {
      this.setSubmitState('submitting');
      this.managementApi.createDBConnection(connectionForm).subscribe(
          (res) => {
              const connection: Partial<Connection> = {
                  name: connectionForm.name,
                  serverAddress: connectionForm.serverAddress,
                  portNumber: connectionForm.portNumber,
                  dbName: connectionForm.dbName,
                  id: res.data.dbConnectionId,
                  isActive: true,
                  workspaceNames: null,
              };
              this.managementState.addConnectionToList(connection);
              this.toastr.showSuccess(res.message);
              this.setSubmitState('submitted');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  loadDataBaseConnection(dbConnectionId: string): void {
      this.managementApi.getDataBaseConnection(dbConnectionId).subscribe(
          (res) => {
              this.managementState.setConnection(res.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  getConnection$(): Observable<Partial<Connection>> {
      return this.managementState.getConnection$();
  }

  updateConnection(connectionForm: Partial<ConnectionForm>, connection: Partial<Connection>): void {
      this.setSubmitState('submitting');
      this.managementApi.updateDBConnection(connectionForm, connection.id).subscribe(
          (res) => {
              this.managementState.updateConnection(connectionForm, connection.id);
              this.managementState.setConnection(null);
              this.toastr.showSuccess(res.message);
              this.setSubmitState('submitted');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  getConnectionTestResponse$(): Observable<ConnectionTestResponse> {
      return this.managementState.getConnectionTestResponse$();
  }

  deleteDataBaseConnection(connection: Partial<Connection>): void {
      this.setSubmitState('submitting');
      this.managementApi.removeDataBaseConnection(connection.id).subscribe(
          (res) => {
              this.managementState.deleteConnection(connection.id);
              this.toastr.showSuccess(res.message);
              this.setSubmitState('submitted');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  updateWorkspacesForDataBaseConnection(workspaces: Partial<Workspace>[], dbConnectionId: string): void {
      this.setSubmitState('submitting');
      const selectedWorkspaces = workspaces.filter(workspace => workspace.hasAccess);
      this.managementApi.grantDataBaseConnectionAccessToWorkspaces(selectedWorkspaces
          .map(workspace => workspace.workspaceId), dbConnectionId).subscribe(
          () => {
              this.managementState.updateConnectionWorkspaces(selectedWorkspaces
                  .map(workspace => workspace.name), dbConnectionId);
              this.setSubmitState('submitted');
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
              this.setSubmitState('notSubmitted');
          },
      );
  }

  loadWorkspacesListForDataBaseConnection(dbConnectionId: string): void {
      this.managementApi.getDataBaseConnectionWorkspaces(dbConnectionId).subscribe(
          (res) => {
              this.updateWorkspacesListForConnection(res.data);
          },
          (errorRes) => {
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  updateWorkspacesListForConnection(workspaces: Partial<Workspace>[]): void {
      this.managementState.updateWorkspacesListForConnection(workspaces);
  }

  getWorkspacesListForConnection$(): Observable<Partial<Workspace>[]> {
      return this.managementState.getWorkspacesListForConnection$();
  }

  changeDataBaseConnectionActivationStatus(connectionId: string, isActive: boolean): void {
      this.managementState.changeConnectionActivationStatus(connectionId, isActive);
      this.managementApi.changeDataBaseConnectionActivationStatus(connectionId, isActive).subscribe(
          (res) => {
              this.toastr.showSuccess(res.message);
          },
          (errorRes) => {
              this.managementState.rollBackConnections();
              this.toastr.showError(errorRes.error.message);
          },
      );
  }

  resetConnectionStates(): void {
      this.managementState.setConnectionTestResponse(null);
  }

  getCustomFields(): void {
    this.managementApi.getCustomFields().subscribe(
        (res) => {
            if (res.isSuccess) {
                this.managementState.setCustomFields(res.data);
            } else {
                this.toastr.showError(res.message);
            }
        },
        (errorRes) => {
            this.toastr.showError(errorRes.error.message);
        }
    );
 }

}
